import { enable2FA, verify2FA } from "@/actions/auth/login";
import { LoadingSpinner } from "@/component/common/LoadingSpinner";
import { useSession } from "@/component/SessionProvider";
import { isActionError } from "@/utils/isActionError";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CheckBadgeIcon } from "@heroicons/react/20/solid";

export const Verify2FA: React.FC = () => {
  const { session, revalidate2FA } = useSession();
  const account = session.user;

  const {
    data,
    mutate: requestOtp,
    isPending,
  } = useMutation({
    mutationKey: ["requestOtp"],
    mutationFn: () => {
      return axios
        .post("/api/request-otp", { accountId: account?.id })
        .then((res) => {
          toast.info(
            `We have sent a code to ${account?.email}, please check your email`
          );
          return res.data;
        });
    },
  });

  const [otp, setOtp] = useState("");

  const { mutate: handle2FAVerify, isPending: isVeryfying } = useMutation({
    mutationKey: ["handle2FAVerify", account?.id],
    mutationFn: async () => {
      const result = await verify2FA(otp);

      if (isActionError(result)) {
        toast.error(result.message);
        return;
      }

      toast.success("2FA verified successfully");
      revalidate2FA();
    },
  });

  const [countDown, setCountDown] = useState(0);
  const expiresAt = new Date(data?.resendTime).getTime();

  const isExpired = countDown <= 0;

  useEffect(() => {
    if (expiresAt) {
      setCountDown(
        Number((expiresAt - new Date().getTime()) / 1000).toFixed(
          0
        ) as unknown as number
      );
      const interval = setInterval(() => {
        setCountDown((prev) => {
          if (prev === 0) {
            clearInterval(interval);
            return 0;
          }

          return Math.max(prev - 1, 0);
        });
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [expiresAt]);

  const disableGetCode = isPending || !isExpired;

  const is2FAVerified = session.is2FAVerified;
  const twoFactorEnabled = account?.is2FAEnabled;

  const [skip2FAEnabler, setSkip2FAEnabler] = useState(twoFactorEnabled);

  if (is2FAVerified) return null;
  if (!twoFactorEnabled && skip2FAEnabler) return null;

  if (!skip2FAEnabler)
    return <ShowEnable2FA setSkip2FAEnabler={setSkip2FAEnabler} />;

  return (
    <div className="fixed bg-slate-900/80 z-50 top-0 left-0 w-full h-full bg-black2 flex items-center justify-center overflow-auto">
      <div className="w-4/6 max-sm:w-full row justify-content-center mt-2">
        <div className="col-lg-8 col-xs-12">
          <div className="eg-login__wrapper grid gap-2">
            <span className="text-lg">Two Factor Authentication</span>
            <span className="text-md text-appPrimary">
              Please enter your authentication code to get access.
            </span>
            <form
              onSubmit={(e) => e.preventDefault()}
              className="newsletter-form"
            >
              <div className="form-group">
                <input
                  className="form-control"
                  type="email"
                  placeholder="Enter Code"
                  onChange={(e) => setOtp(e.target.value)}
                  value={otp}
                />
              </div>

              <button
                onClick={() => requestOtp()}
                type="button"
                className="btn btn5"
                disabled={disableGetCode}
              >
                {isPending && <LoadingSpinner />}
                {countDown ? `Resend in ${countDown}s` : "Get Code"}
              </button>
            </form>
            <div className="flex justify-center mt-2">
              <button
                disabled={isPending || !otp || isVeryfying}
                onClick={() => handle2FAVerify()}
                type="button"
                className="btn btn5 flex justify-between"
              >
                {isVeryfying && <LoadingSpinner />}
                Verify 2FA Code
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ShowEnable2FA = ({
  setSkip2FAEnabler,
}: {
  setSkip2FAEnabler: (show: boolean) => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleEnable2FA = async () => {
    setIsLoading(true);
    enable2FA(true)
      .then((res) => {
        console.log("res", res);
        if (isActionError(res)) {
          return toast.error(res.message);
        }
        toast.success("2FA enabled successfully");
        setSkip2FAEnabler(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  return (
    <div className="fixed bg-slate-900/80 z-50 top-0 left-0 w-full h-full bg-black2 flex items-center justify-center overflow-auto">
      <div className="w-4/6 max-sm:w-full mx-2 justify-content-center mt-2 rounded-lg">
        <div className="eg-login__wrapper max-sm:pb-32 flex max-sm:grid gap-10 outline outline-fuchsia-400/20">
          <div className="w-1/2 max-sm:w-full px-2 grid min-[600px]:text-center min-[600px]:items-center min-[600px]:justify-center">
            {
              // eslint-disable-next-line @next/next/no-img-element
              <img
                className="mx-auto size-40x mb-2"
                src="/assets/img/images/security_1.png"
                alt=""
              />
            }
            <span className="text-2xl font-bold mb-2">
              Protect your BitPrimax account
            </span>
            <span className="text-sm">
              Enable two factor authentication to secure your account on Login.
            </span>
          </div>

          <div className="w-1/2 max-sm:w-full px-2 grid justify-stretch items-stretch">
            <span className="text-xl font-bold mb-4">
              Why you need Two-Factor Authentication
            </span>
            <div className="flex flex-col gap-6 text-sm">
              <span className="flex gap-2">
                <CheckBadgeIcon className="size-8 inline-block text-appPrimary" />
                Adds an extra layer of security to your account by requiring a
                second form of verification.
              </span>
              <span className="flex gap-2">
                <CheckBadgeIcon className="size-8 inline-block text-appPrimary" />
                This helps protect your account from unauthorized access and
                keeps your funds safe.
              </span>
            </div>
            <div className="flex flex-col items-center justify-center mt-4">
              <button
                disabled={isLoading}
                onClick={handleEnable2FA}
                className="btn btn5 mt-4"
              >
                {isLoading && <LoadingSpinner />}
                Enable Two-Factor Authentication
              </button>
              <button
                onClick={() => setSkip2FAEnabler(true)}
                className="text-sm py-1 px-4 cursor-pointer bg-slate-900 w-20 rounded-xl mt-2"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
